// src/config/database/pricingConstantsConfig.js

export const pricingConstantsTableConfig = {
  // Table identification
  id: 'pricing_constants',
  name: 'Pricing Constants',
  apiEndpoint: '/api/admin/pricing-constants',
  
  // Column definitions for the DataTable
  columns: [
    {
      key: 'name',
      label: 'Constant Name',
      sortable: true,
      width: '25%',
      render: (value, record) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{value}</span>
          <span className="text-xs text-gray-500">
            {record.scope === 'global' ? '🌍 Global' : 
             record.scope === 'small_format' ? '📄 Small Format' : 
             '🖼️ Large Format'}
          </span>
        </div>
      )
    },
    {
      key: 'value',
      label: 'Current Value',
      sortable: true,
      width: '15%',
      render: (value, record) => {
        const displayValue = formatConstantValue(record.parsed_value, record.data_type);
        return (
          <div className="flex flex-col">
            <span className="font-mono text-sm text-gray-900 break-all">{displayValue}</span>
            <span className="text-xs text-gray-500">({record.data_type})</span>
          </div>
        );
      }
    },
    {
      key: 'scope',
      label: 'Scope',
      sortable: true,
      width: '15%',
      render: (value) => {
        const scopeStyles = {
          'global': 'bg-purple-100 text-purple-800',
          'small_format': 'bg-blue-100 text-blue-800',
          'large_format': 'bg-green-100 text-green-800'
        };
        
        const scopeLabels = {
          'global': 'Global',
          'small_format': 'Small Format',
          'large_format': 'Large Format'
        };
        
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${scopeStyles[value] || 'bg-gray-100 text-gray-800'}`}>
            {scopeLabels[value] || value}
          </span>
        );
      }
    },
    {
      key: 'data_type',
      label: 'Data Type',
      sortable: true,
      width: '12%',
      render: (value) => {
        const typeStyles = {
          'string': 'bg-gray-100 text-gray-800',
          'number': 'bg-yellow-100 text-yellow-800',
          'boolean': 'bg-indigo-100 text-indigo-800',
          'json': 'bg-red-100 text-red-800'
        };
        
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-md ${typeStyles[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        );
      }
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      width: '25%',
      render: (value) => (
        <span className="text-gray-700 text-sm" title={value}>
          {value || '-'}
        </span>
      )
    },
    {
      key: 'updated_at',
      label: 'Last Modified',
      sortable: true,
      width: '8%',
      render: (value) => (
        <span className="text-gray-500 text-xs">
          {value ? new Date(value).toLocaleDateString() : '-'}
        </span>
      )
    }
  ],

  // Field definitions for the RecordModal form
  fields: [
    {
      key: 'scope',
      label: 'Scope',
      type: 'select',
      required: true,
      options: [
        { value: 'global', label: '🌍 Global (affects both formats)' },
        { value: 'small_format', label: '📄 Small Format only' },
        { value: 'large_format', label: '🖼️ Large Format only' }
      ],
      help: 'Defines which pricing calculations this constant affects'
    },
    {
      key: 'name',
      label: 'Constant Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., minCharge, defaultBleed, setupTimePerJob',
      help: 'Unique identifier for this constant within its scope. Use camelCase.'
    },
    {
      key: 'data_type',
      label: 'Data Type',
      type: 'select',
      required: true,
      options: [
        { value: 'number', label: '🔢 Number (integers and decimals)' },
        { value: 'string', label: '📝 String (text values)' },
        { value: 'boolean', label: '✅ Boolean (true/false)' },
        { value: 'json', label: '📋 JSON (objects and arrays)' }
      ],
      help: 'How the value should be parsed in calculations'
    },
    {
      key: 'value',
      label: 'Value',
      type: 'textarea',
      required: true,
      placeholder: 'Enter the constant value...',
      rows: 3,
      help: 'The actual value. Will be parsed according to the data type selected above.'
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      placeholder: 'Describe what this constant controls and how it affects pricing...',
      rows: 2,
      help: 'Optional description explaining the purpose and usage of this constant'
    }
  ]
};

// Helper function to format constant values for display
function formatConstantValue(parsedValue, dataType) {
  if (parsedValue === null || parsedValue === undefined) {
    return 'null';
  }
  
  switch (dataType) {
    case 'number':
      return typeof parsedValue === 'number' ? parsedValue.toString() : parsedValue;
    case 'boolean':
      return parsedValue ? 'true' : 'false';
    case 'json':
      return typeof parsedValue === 'object' ? JSON.stringify(parsedValue, null, 2) : parsedValue;
    case 'string':
    default:
      return parsedValue.toString();
  }
}

// Transform functions for API communication
export const transformPricingConstantForApi = (formData) => {
  return {
    ...formData,
    // Keep value as-is, backend will handle string storage
    value: formData.value
  };
};

export const transformPricingConstantFromApi = (apiData) => {
  return {
    ...apiData,
    // Convert value to string for form editing
    value: apiData.value ? apiData.value.toString() : ''
  };
};

// Validation rules
export const validatePricingConstant = (data) => {
  const errors = {};
  
  if (!data.scope) {
    errors.scope = 'Scope is required';
  }
  
  if (!data.name || data.name.trim() === '') {
    errors.name = 'Constant name is required';
  } else {
    // Validate name format (should be camelCase, no spaces)
    const namePattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;
    if (!namePattern.test(data.name.trim())) {
      errors.name = 'Name must start with a letter and contain only letters, numbers, and underscores';
    }
  }
  
  if (!data.data_type) {
    errors.data_type = 'Data type is required';
  }
  
  if (!data.value || data.value.trim() === '') {
    errors.value = 'Value is required';
  } else {
    // Validate value format based on data type
    const validationError = validateConstantValueFormat(data.value, data.data_type);
    if (validationError) {
      errors.value = validationError;
    }
  }
  
  return Object.keys(errors).length > 0 ? errors : null;
};

// Helper function to validate value format
function validateConstantValueFormat(value, dataType) {
  try {
    switch (dataType) {
      case 'number':
        const num = parseFloat(value);
        if (isNaN(num)) {
          return 'Value must be a valid number';
        }
        break;
      case 'boolean':
        const lowerValue = value.toLowerCase().trim();
        if (!['true', 'false', '1', '0', 'yes', 'no'].includes(lowerValue)) {
          return 'Boolean value must be true, false, 1, 0, yes, or no';
        }
        break;
      case 'json':
        JSON.parse(value);
        break;
      case 'string':
        // String accepts anything
        break;
      default:
        return 'Invalid data type';
    }
    return null;
  } catch (e) {
    if (dataType === 'json') {
      return 'Value must be valid JSON format';
    }
    return `Invalid ${dataType} format`;
  }
}

// Constants that should not be deleted (used in backend validation)
export const CRITICAL_CONSTANTS = [
  'minCharge', 'defaultBleed', 'setupTimePerJob', 'laborRate', 
  'retailSetup', 'defaultMarkup', 'setup', 'standardGutter_mm', 'global_price_multiplier'
];

// Predefined constant templates for common constants
export const CONSTANT_TEMPLATES = {
  small_format: [
    { name: 'minCharge', data_type: 'number', value: '15', description: 'Minimum charge for small format jobs' },
    { name: 'defaultBleed', data_type: 'number', value: '3', description: 'Default bleed in mm' },
    { name: 'defaultGutter', data_type: 'number', value: '2', description: 'Default gutter in mm' },
    { name: 'setupTimePerJob', data_type: 'number', value: '10', description: 'Setup time per job in minutes' },
    { name: 'laborRate', data_type: 'number', value: '60', description: 'Labor rate per hour' },
    { name: 'retailSetup', data_type: 'number', value: '25', description: 'Flat setup cost for retail pricing' },
    { name: 'defaultMarkup', data_type: 'number', value: '100', description: 'Default markup percentage' }
  ],
  large_format: [
    { name: 'minCharge', data_type: 'number', value: '20', description: 'Minimum charge for large format jobs' },
    { name: 'setup', data_type: 'number', value: '5', description: 'Default job setup charge' },
    { name: 'standardGutter_mm', data_type: 'number', value: '5', description: 'Default standard gutter in mm' }
  ],
  global: [
    { name: 'currency', data_type: 'string', value: 'GBP', description: 'Currency code' },
    { name: 'tax_rate', data_type: 'number', value: '20', description: 'VAT rate percentage' }
  ]
};

export default pricingConstantsTableConfig;
